// src/api/apiClient.ts
// Merged API Client with Strapi v5 functionality

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    ID,
    StrapiResponse,
    StrapiQueryParams,
    ApiError,
    OperationResult,
    BulkOperationResult,
    STRAPI_CONSTANTS,
    isDocumentIdFormat
} from '@/types/common';

export interface SearchOptions {
    populate?: string | string[];
    sort?: string | string[];
    fields?: string[];
  }
  
  export interface PaginationOptions {
    page: number;
    pageSize: number;
  }
  
  export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: any;
  }

/**
 * Default configuration for API client
 */
const defaultConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:1337',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 300000, // 30 seconds
};

/**
 * Enhanced API Client with Strapi v5 capabilities
 * Combines general HTTP client with Strapi-specific operations
 */
class ApiClient {
    public client: AxiosInstance;
    private refreshTokenPromise: Promise<string> | null = null;
    private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

    constructor(config: AxiosRequestConfig = {}) {
        this.client = axios.create({
            ...defaultConfig,
            ...config,
        });

        this.setupInterceptors();
    }

    /**
     * Setup request/response interceptors
     */
    private setupInterceptors(): void {
        // Request interceptor - add token to headers
        this.client.interceptors.request.use(
            (config) => {
                // Get token from localStorage
                const token = localStorage.getItem('token');

                // Only add Authorization header when token exists
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor - handle responses and errors
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                // Get original request config and response
                const originalRequest = error.config;
                const { status } = error.response || {};

                // Handle expired token (401)
                if (status === 401 && !originalRequest._retry) {
                    // Mark this request as already retried
                    originalRequest._retry = true;

                    try {
                        // Try to refresh token
                        const newToken = await this.refreshToken();

                        // Update token for current request
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;

                        // Retry request with new token
                        return this.client(originalRequest);
                    } catch (refreshError) {
                        // If can't refresh token, logout user
                        localStorage.removeItem('token');
                        localStorage.removeItem('userId');
                        localStorage.removeItem('userRole');
                        localStorage.removeItem('refreshToken');

                        // Redirect to login page
                        window.location.href = '/login';

                        return Promise.reject(refreshError);
                    }
                }

                // Log Strapi API errors for debugging
                console.error('📡 API Error:', error.response?.data || error.message);

                // Handle other 4xx and 5xx errors
                return Promise.reject(this.createApiError(error));
            }
        );
    }

    /**
     * Perform token refresh
     */
    async refreshToken(): Promise<string> {
        // If there's already a refresh token promise, return it
        if (this.refreshTokenPromise) {
            return this.refreshTokenPromise;
        }

        // Create new promise to refresh token
        this.refreshTokenPromise = new Promise<string>(async (resolve, reject) => {
            try {
                // Call refresh token API
                const response = await this.client.post('/api/auth/refresh-token', {
                    refreshToken: localStorage.getItem('refreshToken')
                });

                // Save new token to localStorage
                const { jwt } = response.data;
                localStorage.setItem('token', jwt);

                // Resolve promise with new token
                resolve(jwt);
            } catch (error) {
                reject(error);
            } finally {
                // Reset promise
                this.refreshTokenPromise = null;
            }
        });

        return this.refreshTokenPromise;
    }

    /**
     * Update authorization token
     */
    public setToken(token: string): void {
        localStorage.setItem('token', token);
        this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    /**
     * Remove authorization token
     */
    public clearToken(): void {
        localStorage.removeItem('token');
        delete this.client.defaults.headers.common['Authorization'];
    }

  /**
   * Delete record by ID
   */
  async deleteRecord(
    collection: string,
    id: string
  ): Promise<ApiResponse<void>> {
    try {
      await this.client.delete(`/${collection}/${id}`);

      return {
        success: true
      };
    } catch (error: any) {
      console.error(`Delete ${collection} error:`, error);
      return {
        success: false,
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Fetch multiple records with pagination
   */
  async fetchRecords<T>(
    collection: string,
    params?: Record<string, any>
  ): Promise<ApiResponse<{ data: T[]; meta: any }>> {
    try {
      const response = await this.client.get(`/${collection}`, { params });

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error(`Fetch ${collection} error:`, error);
      return {
        success: false,
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Custom API call
   */
  async customRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    data?: any,
    params?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request({
        method,
        url,
        data,
        params
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error(`Custom request error:`, error);
      return {
        success: false,
        error: error.response?.data || error.message
      };
    }
  }
  
    // ========================================
    // GENERAL HTTP METHODS
    // ========================================

    /**
     * Send GET request
     */
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.get<T>(url, config);
    }

    /**
     * Send POST request
     */
    post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post<T>(url, data, config);
    }

    /**
     * Send PUT request
     */
    put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.put<T>(url, data, config);
    }

    /**
     * Send PATCH request
     */
    patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.patch<T>(url, data, config);
    }

    /**
     * Send DELETE request
     */
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.delete<T>(url, config);
    }

    /**
     * Upload request with FormData
     */
    upload<T = any>(
        method: string,
        url: string,
        formData: FormData,
        onUploadProgress?: (progressEvent: any) => void
    ): Promise<AxiosResponse<T>> {
        return this.client.request<T>({
            method,
            url,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress
        });
    }

    // ========================================
    // STRAPI v5 SPECIFIC METHODS
    // ========================================

    /**
     * Get documentId from record ID
     */
    public async getDocumentId(
        schema: string,
        recordId: string | number
    ): Promise<string> {
        try {
            // Already documentId format? Validate and return
            if (typeof recordId === 'string' && isDocumentIdFormat(recordId)) {
                console.log(`✅ Already documentId format: ${recordId}`);
                return recordId;
            }

            console.log(`🔍 Getting documentId for ${schema} record ${recordId}`);

            const response = await this.client.get(`/api/${schema}s/${recordId}`);
            const documentId = response.data?.data?.documentId;

            if (!documentId) {
                console.warn(`⚠️ No documentId found for ${schema} record ${recordId}`);
                return recordId.toString();
            }

            console.log(`✅ Found documentId: ${documentId} for record ${recordId}`);
            return documentId;
        } catch (error) {
            console.warn(`❌ Failed to get documentId for ${schema} record ${recordId}:`, error);
            return recordId.toString();
        }
    }

    /**
     * Get regular ID from documentId
     */
    public async getRegularId(
        schema: string,
        documentId: string
    ): Promise<number | string> {
        try {
            console.log(`🔍 Getting regular ID for ${schema} documentId ${documentId}`);

            const response = await this.client.get(`/api/${schema}s/${documentId}`);
            const regularId = response.data?.data?.id;

            if (!regularId) {
                console.warn(`⚠️ No regular ID found for ${schema} documentId ${documentId}`);
                return documentId;
            }

            console.log(`✅ Found regular ID: ${regularId} for documentId ${documentId}`);
            return regularId;
        } catch (error) {
            console.warn(`❌ Failed to get regular ID for ${schema} documentId ${documentId}:`, error);
            return documentId;
        }
    }

    /**
     * Get optimal ID for operations
     */
    public getOptimalId(record: any, operation: 'update' | 'delete' | 'get'): string {
        const documentId = record?.documentId;
        const regularId = record?.id;

        switch (operation) {
            case 'update':
                // UPDATE works best with regular ID in Strapi v5
                return regularId?.toString() || documentId || '';
            case 'delete':
                // DELETE works best with documentId in Strapi v5
                return documentId || regularId?.toString() || '';
            case 'get':
            default:
                // GET works with both, prefer documentId
                return documentId || regularId?.toString() || '';
        }
    }

    /**
     * Update record by ID
     */
    public async updateRecordById<T = any>(
        schema: string,
        recordId: string | number,
        updateData: Partial<T>
    ): Promise<OperationResult<T>> {
        const startTime = Date.now();

        try {
            console.log(`🔄 Updating ${schema} record ${recordId}`);

            // Get regular ID for UPDATE operations (tested strategy)
            let actualId = recordId;
            if (isDocumentIdFormat(recordId.toString())) {
                try {
                    actualId = await this.getRegularId(schema, recordId.toString());
                    console.log(`✅ Using regular ID for UPDATE: ${actualId}`);
                } catch {
                    console.warn(`⚠️ Could not get regular ID, using original: ${recordId}`);
                    actualId = recordId;
                }
            }

            const response = await this.client.put(`/api/${schema}s/${actualId}`, {
                data: updateData
            });

            console.log(`✅ Successfully updated ${schema} record with ID: ${actualId}`);

            // Clear related cache
            this.clearCachePattern(schema);

            return {
                success: true,
                data: response.data?.data,
                metadata: {
                    operation: 'update',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId: actualId,
                    schema
                }
            };
        } catch (error) {
            return {
                success: false,
                error: this.createApiError(error as any),
                metadata: {
                    operation: 'update',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId,
                    schema
                }
            };
        }
    }

    /**
     * Delete record by ID
     */
    public async deleteRecordById<T = any>(
        schema: string,
        recordId: string | number
    ): Promise<OperationResult<T>> {
        const startTime = Date.now();

        try {
            console.log(`🗑️ Deleting ${schema} record ${recordId}`);

            // Get documentId for DELETE operations (tested strategy)
            let actualId = recordId;
            if (!isDocumentIdFormat(recordId.toString())) {
                try {
                    actualId = await this.getDocumentId(schema, recordId);
                    console.log(`✅ Using documentId for DELETE: ${actualId}`);
                } catch {
                    console.warn(`⚠️ Could not get documentId, using original: ${recordId}`);
                    actualId = recordId;
                }
            }

            const response = await this.client.delete(`/api/${schema}s/${actualId}`);

            console.log(`✅ Successfully deleted ${schema} record with ID: ${actualId}`);

            // Clear related cache
            this.clearCachePattern(schema);

            return {
                success: true,
                data: response.data?.data,
                metadata: {
                    operation: 'delete',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId: actualId,
                    schema
                }
            };
        } catch (error) {
            return {
                success: false,
                error: this.createApiError(error as any),
                metadata: {
                    operation: 'delete',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId,
                    schema
                }
            };
        }
    }

    /**
     * Fetch single record
     */
    public async fetchRecord<T = any>(
        schema: string,
        identifier: string | number,
        params?: StrapiQueryParams
    ): Promise<OperationResult<T>> {
        const startTime = Date.now();
        const cacheKey = `${schema}:${identifier}:${JSON.stringify(params)}`;

        try {
            // Check cache first
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log(`📦 Cache hit for ${schema} record ${identifier}`);
                return {
                    success: true,
                    data: cached,
                    metadata: {
                        operation: 'get',
                        timestamp: new Date().toISOString(),
                        duration: Date.now() - startTime,
                        recordId: identifier,
                        schema
                    }
                };
            }

            console.log(`🔍 Fetching ${schema} record: ${identifier}`);

            const response = await this.client.get(`/api/${schema}s/${identifier}`, {
                params: this.buildQueryParams(params)
            });

            const data = response.data?.data;

            // Cache the result
            this.setCachedData(cacheKey, data);

            console.log(`✅ Successfully fetched ${schema} record`);

            return {
                success: true,
                data,
                metadata: {
                    operation: 'get',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId: identifier,
                    schema
                }
            };
        } catch (error) {
            return {
                success: false,
                error: this.createApiError(error as any),
                metadata: {
                    operation: 'get',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    recordId: identifier,
                    schema
                }
            };
        }
    }

    /**
     * Search records with filters
     */
    public async searchRecords<T = any>(
        schema: string,
        filters: Record<string, any> = {},
        pagination: { page?: number; pageSize?: number } = {},
        params?: Omit<StrapiQueryParams, 'filters' | 'pagination'>
    ): Promise<OperationResult<{ data: T[]; meta: any }>> {
        const startTime = Date.now();

        try {
            console.log(`🔍 Searching ${schema} records with filters:`, filters);

            const queryParams = this.buildQueryParams({
                ...params,
                filters,
                pagination
            });

            const response = await this.client.get(`/api/${schema}s`, {
                params: queryParams
            });

            const result = {
                data: response.data?.data || [],
                meta: response.data?.meta || {}
            };

            console.log(`✅ Found ${result.data.length} ${schema} records`);

            return {
                success: true,
                data: result,
                metadata: {
                    operation: 'search',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    schema
                }
            };
        } catch (error) {
            return {
                success: false,
                error: this.createApiError(error as any),
                metadata: {
                    operation: 'search',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    schema
                }
            };
        }
    }

    /**
     * Create new record
     */
    public async createRecord<T = any>(
        schema: string,
        data: Partial<T>
    ): Promise<OperationResult<T>> {
        const startTime = Date.now();

        try {
            console.log(`➕ Creating new ${schema} record`);

            const response = await this.client.post(`/api/${schema}s`, {
                data: {
                    ...data,
                    
                }
            });

            console.log(`✅ Successfully created ${schema} record`);

            // Clear related cache
            this.clearCachePattern(schema);

            return {
                success: true,
                data: response.data?.data,
                metadata: {
                    operation: 'create',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    schema
                }
            };
        } catch (error) {
            return {
                success: false,
                error: this.createApiError(error as any),
                metadata: {
                    operation: 'create',
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime,
                    schema
                }
            };
        }
    }

    /**
     * Bulk update records
     */
    public async bulkUpdateRecords<T = any>(
        schema: string,
        recordIds: (string | number)[],
        updateData: Partial<T>
    ): Promise<BulkOperationResult<T>> {
        console.log(`🔄 Bulk updating ${recordIds.length} ${schema} records`);

        // Process in chunks to avoid overwhelming the server
        const chunkSize = Math.min(STRAPI_CONSTANTS.MAX_BULK_OPERATION_SIZE, 10);
        const chunks = this.chunkArray(recordIds, chunkSize);
        const results = [];

        for (const chunk of chunks) {
            // Process chunk with rate limiting
            const chunkPromises = chunk.map(async id => {
                const result = await this.updateRecordById(schema, id, updateData);
                return {
                    id,
                    success: result.success,
                    data: result.data,
                    error: result.error?.message
                };
            });

            const chunkResults = await Promise.all(chunkPromises);
            results.push(...chunkResults);

            // Rate limiting delay between chunks
            if (chunks.indexOf(chunk) < chunks.length - 1) {
                await this.delay(200); // 200ms delay between chunks
            }
        }

        const successCount = results.filter(r => r.success).length;
        console.log(`✅ Bulk update completed: ${successCount}/${recordIds.length} successful`);

        return {
            results,
            summary: {
                total: recordIds.length,
                successful: successCount,
                failed: recordIds.length - successCount
            }
        };
    }

    /**
     * Bulk delete records
     */
    public async bulkDeleteRecords(
        schema: string,
        recordIds: (string | number)[]
    ): Promise<BulkOperationResult> {
        console.log(`🗑️ Bulk deleting ${recordIds.length} ${schema} records`);

        const chunkSize = Math.min(STRAPI_CONSTANTS.MAX_BULK_OPERATION_SIZE, 10);
        const chunks = this.chunkArray(recordIds, chunkSize);
        const results = [];

        for (const chunk of chunks) {
            const chunkPromises = chunk.map(async id => {
                const result = await this.deleteRecordById(schema, id);
                return {
                    id,
                    success: result.success,
                    error: result.error?.message
                };
            });

            const chunkResults = await Promise.all(chunkPromises);
            results.push(...chunkResults);

            // Rate limiting delay
            if (chunks.indexOf(chunk) < chunks.length - 1) {
                await this.delay(200);
            }
        }

        const successCount = results.filter(r => r.success).length;
        console.log(`✅ Bulk delete completed: ${successCount}/${recordIds.length} successful`);

        return {
            results,
            summary: {
                total: recordIds.length,
                successful: successCount,
                failed: recordIds.length - successCount
            }
        };
    }

    /**
     * Get Strapi error message
     */
    public getStrapiErrorMessage(error: any): string {
        let errorMessage = 'Có lỗi xảy ra. Vui lòng thử lại sau.';

        if (error.response?.data) {
            const { data } = error.response;

            if (data.error) {
                if (typeof data.error === 'string') {
                    errorMessage = data.error;
                } else if (data.error.message) {
                    errorMessage = data.error.message;

                    // Handle validation errors
                    if (data.error.details?.errors) {
                        const validationErrors = data.error.details.errors
                            .map((e: any) => {
                                const field = e.path?.join('.') || 'Unknown field';
                                return `${field}: ${e.message}`;
                            })
                            .join('; ');

                        errorMessage = `${errorMessage}. Chi tiết: ${validationErrors}`;
                    }
                }
            } else if (data.message) {
                errorMessage = data.message;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }

        // Localize Strapi v5 specific errors
        if (errorMessage.includes('documentId')) {
            errorMessage = `Lỗi documentId (Strapi v5): ${errorMessage}`;
        }

        if (errorMessage.includes('MinIO') || errorMessage.includes('S3')) {
            errorMessage = `Lỗi lưu trữ file: ${errorMessage}`;
        }

        return errorMessage;
    }

    // ========================================
    // UTILITY METHODS
    // ========================================

    /**
     * Validate schema name for Strapi API
     */
    public validateSchemaName(schema: string): boolean {
        const validPattern = /^[a-z][a-z0-9-]*$/;
        return validPattern.test(schema);
    }

    /**
     * Get schema endpoint URL
     */
    public getSchemaEndpoint(schema: string): string {
        if (!this.validateSchemaName(schema)) {
            throw new Error(`Invalid schema name: ${schema}`);
        }
        return `/api/${schema}s`;
    }

    /**
     * Clear cache for specific key or all cache
     */
    public clearCache(pattern?: string): void {
        this.clearCachePattern(pattern);
    }

    // ========================================
    // PRIVATE HELPER METHODS
    // ========================================

    /**
     * Create standardized API error
     */
    private createApiError(error: any): ApiError {
        const apiError = new Error(this.getStrapiErrorMessage(error)) as ApiError;
        apiError.statusCode = error.response?.status;
        apiError.code = error.code;
        apiError.response = error.response;
        apiError.details = error.response?.data?.error?.details;
        return apiError;
    }

    /**
     * Build query parameters for Strapi API
     */
    private buildQueryParams(params?: StrapiQueryParams): Record<string, any> {
        if (!params) return {};

        const queryParams: Record<string, any> = {};

        // Handle populate
        if (params.populate) {
            queryParams.populate = params.populate;
        }

        // Handle filters
        if (params.filters) {
            Object.keys(params.filters).forEach(key => {
                if (typeof params.filters![key] === 'object' && params.filters![key] !== null) {
                    // Complex filter object
                    Object.keys(params.filters![key]).forEach(operator => {
                        queryParams[`filters[${key}][${operator}]`] = params.filters![key][operator];
                    });
                } else {
                    // Simple equality filter
                    queryParams[`filters[${key}][$eq]`] = params.filters![key];
                }
            });
        }

        // Handle sorting
        if (params.sort) {
            queryParams.sort = params.sort;
        }

        // Handle pagination
        if (params.pagination) {
            Object.keys(params.pagination).forEach(key => {
                queryParams[key] = params.pagination![key as keyof typeof params.pagination];
            });
        }

        // Handle other parameters
        if (params.locale) queryParams.locale = params.locale;
        if (params.fields) queryParams.fields = params.fields;
        if (params.publicationState) queryParams.publicationState = params.publicationState;

        return queryParams;
    }

    /**
     * Get cached data if not expired
     */
    private getCachedData(key: string): any {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < cached.ttl) {
            return cached.data;
        }
        if (cached) {
            this.cache.delete(key);
        }
        return null;
    }

    /**
     * Set data in cache with TTL
     */
    private setCachedData(key: string, data: any, ttl: number = STRAPI_CONSTANTS.DEFAULT_CACHE_TTL): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl
        });
    }

    /**
     * Clear cache by pattern
     */
    private clearCachePattern(pattern?: string): void {
        if (pattern) {
            for (const key of this.cache.keys()) {
                if (key.includes(pattern)) {
                    this.cache.delete(key);
                }
            }
        } else {
            this.cache.clear();
        }
    }

    /**
     * Split array into chunks
     */
    private chunkArray<T>(array: T[], size: number): T[][] {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }

    /**
     * Delay execution for rate limiting
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// ========================================
// LEGACY FUNCTION WRAPPERS
// ========================================

/**
 * Create default API client instance
 */
const apiClient = new ApiClient();

/**
 * Legacy function wrapper - get documentId
 */
export const getDocumentId = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number
): Promise<string> => {
    // Create temporary client if different baseURL
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        return tempClient.getDocumentId(schema, recordId);
    }

    return apiClient.getDocumentId(schema, recordId);
};

/**
 * Legacy function wrapper - get regular ID
 */
export const getRegularId = async (
    baseURL: string,
    token: string | null,
    schema: string,
    documentId: string
): Promise<number | string> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        return tempClient.getRegularId(schema, documentId);
    }

    return apiClient.getRegularId(schema, documentId);
};

/**
 * Legacy function wrapper - update record by ID
 */
export const updateRecordById = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number,
    updateData: any
): Promise<any> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        const result = await tempClient.updateRecordById(schema, recordId, updateData);
        if (!result.success) throw result.error;
        return { data: result.data };
    }

    const result = await apiClient.updateRecordById(schema, recordId, updateData);
    if (!result.success) throw result.error;
    return { data: result.data };
};

/**
 * Legacy function wrapper - delete record by ID
 */
export const deleteRecordById = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number
): Promise<any> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        const result = await tempClient.deleteRecordById(schema, recordId);
        if (!result.success) throw result.error;
        return { data: result.data };
    }

    const result = await apiClient.deleteRecordById(schema, recordId);
    if (!result.success) throw result.error;
    return { data: result.data };
};

/**
 * Legacy function wrapper - bulk update records
 */
export const bulkUpdateRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordIds: (string | number)[],
    updateData: any
): Promise<BulkOperationResult> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        return tempClient.bulkUpdateRecords(schema, recordIds, updateData);
    }

    return apiClient.bulkUpdateRecords(schema, recordIds, updateData);
};

/**
 * Legacy function wrapper - bulk delete records
 */
export const bulkDeleteRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    recordIds: (string | number)[]
): Promise<BulkOperationResult> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        return tempClient.bulkDeleteRecords(schema, recordIds);
    }

    return apiClient.bulkDeleteRecords(schema, recordIds);
};

/**
 * Legacy function wrapper - fetch record
 */
export const fetchRecord = async (
    baseURL: string,
    token: string | null,
    schema: string,
    identifier: string | number,
    populate: string = '*'
): Promise<any> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        const result = await tempClient.fetchRecord(schema, identifier, { populate });
        if (!result.success) throw result.error;
        return result.data;
    }

    const result = await apiClient.fetchRecord(schema, identifier, { populate });
    if (!result.success) throw result.error;
    return result.data;
};

/**
 * Legacy function wrapper - search records
 */
export const searchRecords = async (
    baseURL: string,
    token: string | null,
    schema: string,
    filters: Record<string, any> = {},
    pagination: { page?: number; pageSize?: number } = {},
    populate: string = '*'
): Promise<{ data: any[], meta: any }> => {
    if (baseURL !== apiClient.client.defaults.baseURL) {
        const tempClient = new ApiClient({ baseURL });
        if (token) tempClient.setToken(token);
        const result = await tempClient.searchRecords(schema, filters, pagination, { populate });
        if (!result.success) throw result.error;
        return result.data!;
    }

    const result = await apiClient.searchRecords(schema, filters, pagination, { populate });
    if (!result.success) throw result.error;
    return result.data!;
};

/**
 * Legacy function wrapper - get Strapi error message
 */
export const getStrapiErrorMessage = (error: any): string => {
    return apiClient.getStrapiErrorMessage(error);
};

// ========================================
// ADDITIONAL UTILITY FUNCTIONS
// ========================================

/**
 * Check if identifier is documentId format
 */
export const isDocumentId = (identifier: string | number): boolean => {
    return isDocumentIdFormat(identifier.toString());
};

/**
 * Get record identifier (documentId if available, otherwise id)
 */
export const getRecordIdentifier = (record: any): string => {
    return record?.documentId || record?.id?.toString() || '';
};

/**
 * Get optimal ID for operations
 */
export const getOptimalId = (record: any, operation: 'update' | 'delete' | 'get'): string => {
    return apiClient.getOptimalId(record, operation);
};

/**
 * Validate schema name
 */
export const validateSchemaName = (schema: string): boolean => {
    return apiClient.validateSchemaName(schema);
};

/**
 * Get schema endpoint URL
 */
export const getSchemaEndpoint = (baseURL: string, schema: string): string => {
    if (!validateSchemaName(schema)) {
        throw new Error(`Invalid schema name: ${schema}`);
    }
    return `${baseURL}/api/${schema}s`;
};

/**
 * Debug and analysis helpers
 */
export const analyzeRecordId = (record: any) => {
    const id = record?.id;
    const documentId = record?.documentId;

    return {
        hasId: Boolean(id),
        hasDocumentId: Boolean(documentId),
        idType: typeof id,
        documentIdType: typeof documentId,
        isDocumentIdFormat: documentId ? isDocumentIdFormat(documentId) : false,
        recommendedForUpdate: id?.toString() || documentId,
        recommendedForDelete: documentId || id?.toString(),
        recommendedForGet: documentId || id?.toString()
    };
};

// ========================================
// EXPORTS
// ========================================

// Export the main API client instance (default behavior)
export default apiClient;

// Export the ApiClient class for advanced usage
export { ApiClient };

// Export function to create new client instances
export const createApiClient = (config?: AxiosRequestConfig): ApiClient => {
    return new ApiClient(config);
};

// Legacy compatibility - export all Strapi functions
export const strapiUtils = {
    // Core functions
    getDocumentId,
    getRegularId,
    updateRecordById,
    deleteRecordById,
    bulkUpdateRecords,
    bulkDeleteRecords,
    fetchRecord,
    searchRecords,
    getStrapiErrorMessage,

    // Utility functions
    isDocumentId,
    getRecordIdentifier,
    getOptimalId,
    validateSchemaName,
    getSchemaEndpoint,
    analyzeRecordId
};

// Re-export commonly used types for convenience
export type {
    ID,
    StrapiResponse,
    StrapiQueryParams,
    ApiError,
    OperationResult,
    BulkOperationResult
} from '@/types/common';
